import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiFileText, FiTrash2, FiRefreshCw, FiShare2 as FiUpload } from 'react-icons/fi';
import { handleUnauthorized } from '../../utils/apiClient';
import '../commissionscan/CommissionScan.css';
import '../policycomparison/PolicyComparison.css';
import '../../styles/QuoteProposal.css';
import '../../styles/CarrierFinder.css';
const getToken = (): string => localStorage.getItem('toolboxToken') || '';
const MAX_FILES = 10;

interface UploadedFile {
  id: string;
  fileName: string;
  isFileUploaded: boolean;
  fileSize: number;
  uploadedDate: string;
}

interface Toast { id: number; type: 'success' | 'error' | 'info'; message: string; }

const CarrierFinderConfigure: React.FC = () => {
  const [staged, setStaged] = useState<File[]>([]);
  const [drag, setDrag] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const toastCounter = useRef(0);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastCounter.current;
    setToasts(p => [...p, { id, type, message }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);

  const fetchFiles = useCallback(() => {
    setListLoading(true);
    axios.get(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/getallfiles`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        setUploadedFiles(Array.isArray(res.data?.response) ? res.data.response : []);
        setListLoading(false);
      })
      .catch(err => {
        setListLoading(false);
        if (err.response?.status === 401) handleUnauthorized();
        else showToast('error', 'Failed to load uploaded files.');
      });
  }, [showToast]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const acceptFiles = (files: File[]) => {
    const valid = files.filter(f => {
      const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) { showToast('error', `"${f.name}" is not a PDF.`); return false; }
      return true;
    });
    setStaged(prev => {
      const existing = new Set(prev.map(p => p.name));
      const fresh = valid.filter(f => !existing.has(f.name));
      const combined = [...prev, ...fresh];
      if (combined.length > MAX_FILES) {
        showToast('info', `Maximum ${MAX_FILES} files allowed. Extra files were skipped.`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  };

  const upload = () => {
    if (staged.length === 0) { showToast('info', 'Select at least one PDF to upload.'); return; }
    setUploading(true);
    const formData = new FormData();
    staged.forEach(f => formData.append('files', f));
    formData.append('uploadedFrom', 'carrier-finder');
    formData.append('userPrompt', instructions.trim());

    axios.post(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/documents-upload`, formData, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(() => {
        showToast('success', 'Documents uploaded successfully!');
        setStaged([]);
        setInstructions('');
        fetchFiles();
        setUploading(false);
      })
      .catch(err => {
        setUploading(false);
        if (err.response?.status === 401) handleUnauthorized();
        else showToast('error', 'Upload failed. Please try again.');
      });
  };

  const deleteFile = (id: string) => {
    setDeletingId(id);
    axios.post(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/delete?id=${id}`, null, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(() => {
        setUploadedFiles(prev => prev.filter(f => f.id !== id));
        showToast('success', 'File deleted.');
        setDeletingId(null);
      })
      .catch(err => {
        setDeletingId(null);
        if (err.response?.status === 401) handleUnauthorized();
        else showToast('error', 'Failed to delete file.');
      });
  };


  return (
    <div className="cs-upload-container">

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
          Configure Carrier Documents
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
          Upload carrier appetite PDFs. The AI will analyze them to match risks with the right carriers.
        </p>
      </div>

      {/* ── Drop Zone ── */}
      <div
        className={`cs-dropzone${drag ? ' cs-dropzone--dragging' : ''}`}
        style={{ gap: 8 }}
        onDrop={e => { e.preventDefault(); setDrag(false); acceptFiles(Array.from(e.dataTransfer.files)); }}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
      >
        <p className="cs-dropzone__title">Drag & Drop your PDF files here</p>
        <p className="cs-dropzone__or">or</p>
        <button type="button" className="cs-browse-btn" onClick={() => fileRef.current?.click()}>
          <FiUpload size={14} style={{ marginRight: 6 }} />
          Browse Files
        </button>
        <p className="cs-dropzone__types">Allowed file types: <strong>PDF</strong></p>

        {/* Selected files — inside the dropzone, below the browse UI */}
        {staged.length > 0 && (
          <div style={{ width: '100%', marginTop: 16, borderTop: '1px solid var(--border-glass)', paddingTop: 16 }}>
            <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              Selected Files ({staged.length})
            </p>
            <div className="cf-staged-grid">
              {staged.map((f, i) => (
                <div key={i} className="cf-staged-card">
                  <div className="cf-staged-card__icon"><FiFileText size={20} /></div>
                  <div className="cf-staged-card__info">
                    <span className="cf-staged-card__name" title={f.name}>{f.name}</span>
                    <span className="cf-staged-card__size">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button
                    className="cf-staged-card__delete"
                    onClick={() => setStaged(p => p.filter((_, j) => j !== i))}
                    title="Remove"
                  >
                    <FiTrash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            {/* Upload button — right-aligned at end of selected files */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="csh-refresh-btn" disabled={uploading} onClick={upload}>
                {uploading
                  ? <><span className="pc-spinner" style={{ width: 14, height: 14 }} /> Uploading…</>
                  : <>Upload</>
                }
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          multiple
          style={{ display: 'none' }}
          onChange={e => { acceptFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
        />
      </div>

      {/* ── Carrier Search Instructions ── */}
      <div style={{ marginTop: 16 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
          Carrier Search Instructions <span style={{ fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          className="cf-chat__input"
          style={{ width: '100%', minHeight: 72, resize: 'vertical', boxSizing: 'border-box' }}
          placeholder="Act as a senior commercial insurance underwriter. Analyze risk appetite by class code, state, and revenue. Provide structured output with eligibility, restrictions, and next steps."
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
        />
      </div>

      {/* ── Uploaded Files Table ── */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Uploaded Documents
            {uploadedFiles.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)' }}>
                ({uploadedFiles.length})
              </span>
            )}
          </h4>
          <button className="csh-refresh-btn" onClick={fetchFiles} disabled={listLoading}>
            <FiRefreshCw size={12} /> Refresh
          </button>
        </div>

        {listLoading ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            <span className="pc-spinner" style={{ width: 16, height: 16, display: 'inline-block', marginRight: 8 }} />
            Loading…
          </div>
        ) : uploadedFiles.length === 0 ? (
          <div className="qp-empty-state">
            <FiFileText size={24} />
            <p>No documents uploaded yet.</p>
          </div>
        ) : (
          <div className="qp-table-wrap glass-surface">
            <table className="qp-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '100%' }} />
                <col style={{ width: 60 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="qp-table__th">File Name</th>
                  <th className="qp-table__th" style={{ textAlign: 'center' }}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map(f => (
                  <tr key={f.id} className="qp-table__row">
                    <td
                      className="qp-table__td"
                      style={{ color: 'var(--accent-purple-light)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={f.fileName}
                    >
                      <FiFileText size={13} style={{ marginRight: 6, verticalAlign: 'middle', opacity: 0.7 }} />
                      {f.fileName}
                    </td>
                    <td className="qp-table__td" style={{ textAlign: 'center' }}>
                      <button
                        className="qp-table__action-btn"
                        title="Delete file"
                        disabled={deletingId === f.id}
                        onClick={() => deleteFile(f.id)}
                        style={{ color: '#ef4444' }}
                      >
                        {deletingId === f.id
                          ? <span className="pc-spinner" style={{ width: 14, height: 14 }} />
                          : <FiTrash2 size={14} />
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Toasts ── */}
      <div className="pc-toast">
        {toasts.map(t => (
          <div key={t.id} className={`pc-toast__item pc-toast__item--${t.type}`}>{t.message}</div>
        ))}
      </div>
    </div>
  );
};

export default CarrierFinderConfigure;

